import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, GitMerge, Loader2, ShieldAlert, Trash2, Check, Building2, Users, Map, MapPin, Archive, Layers, ChevronDown, ChevronUp, Download, Search, X, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, downloadBlob } from "../lib/api";

/** Master types that support duplicate detection + merge (Phase 116). */
export const MERGEABLE_MASTERS = { zones: "zone", wards: "ward", mohallas: "mohalla" };

const humanField = (f) => f.replace("property_doc.", "trash › ").replace(/_/g, " ");

/** Impact buckets returned by the backend (`report.impact`) -> icon + testid. Order = display order. */
const IMPACT_ORDER = [
  { key: "properties", icon: Building2 },
  { key: "properties_trash", icon: Archive },
  { key: "staff", icon: Users },
  { key: "wards", icon: Map },
  { key: "mohallas", icon: MapPin },
  { key: "other", icon: Layers },
];
const FALLBACK_LABELS = { properties: "Properties", properties_trash: "Properties in trash", staff: "Staff mappings", wards: "Wards", mohallas: "Mohallas", other: "Other records" };
/** Phase 162: chips that open the property-level preview underneath (bucket -> `trash` query flag). */
const DRILLABLE = { properties: false, properties_trash: true };
const PAGE_SIZE = 25;
const n = (v) => Number(v || 0).toLocaleString("en-IN");

/** "Who is affected" summary chips — distinct records per bucket, rolled up across child merges.
 *  Phase 162: the Properties / Properties-in-trash chips are buttons that expand the affected list below. */
function ImpactSummary({ report, openBucket, onToggle }) {
  const impact = report?.impact || {};
  const labels = { ...FALLBACK_LABELS, ...(report?.impact_labels || {}) };
  const chips = IMPACT_ORDER.filter(({ key }) => (impact[key] || 0) > 0);
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-2 px-3 py-2.5 border-b" data-testid="merge-impact-summary">
      {chips.map(({ key, icon: Icon }) => {
        const drill = key in DRILLABLE;
        const active = openBucket === key;
        const inner = (
          <>
            <Icon className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            <span className="font-semibold tabular-nums">{n(impact[key])}</span>
            <span className={active ? "text-foreground" : "text-muted-foreground"}>{labels[key]}</span>
            {drill && (active ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />)}
          </>
        );
        if (!drill) {
          return (
            <span key={key} className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs" data-testid={`merge-impact-${key}`}>
              {inner}
            </span>
          );
        }
        return (
          <button key={key} type="button" onClick={() => onToggle(active ? null : key)} aria-expanded={active} aria-controls="merge-affected-panel"
            title={`Show the ${labels[key].toLowerCase()} this merge will change`}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs cursor-pointer transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${active ? "border-primary bg-primary/5 shadow-sm" : "bg-background"}`}
            data-testid={`merge-impact-${key}`} data-state={active ? "open" : "closed"}>
            {inner}
          </button>
        );
      })}
    </div>
  );
}

/** One "Current → After merge" cell: every level that changes for this property, the merged level first. */
function ChangeCell({ item, levelKey }) {
  const changes = item.changes || [];
  if (!changes.length) {
    return <span className="text-xs text-muted-foreground">No change</span>;
  }
  const order = { [levelKey]: 0 };
  const sorted = [...changes].sort((a, b) => (order[a.level] ?? 1) - (order[b.level] ?? 1));
  return (
    <div className="space-y-0.5">
      {sorted.map((c, i) => (
        <div key={i} className="flex flex-wrap items-center gap-x-1.5 text-xs leading-snug" data-testid={`merge-affected-change-${c.level}`}>
          {sorted.length > 1 && <span className="text-muted-foreground">{c.label}:</span>}
          <span className="line-through decoration-destructive/60 text-muted-foreground">{c.from?.label || "—"}</span>
          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" aria-hidden="true" />
          <span className="font-medium text-foreground">{c.to?.label || "—"}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Phase 162 — inline, expandable list of every property the merge (and its cascades) will change.
 * props: code, masterType, sourceId, targetId, trash, expected (chip count), onClose
 */
function AffectedPropertiesPanel({ code, masterType, sourceId, targetId, trash, expected, onClose }) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [tick, setTick] = useState(0);
  const levelKey = masterType === "zones" ? "zone" : masterType === "wards" ? "ward" : "mohalla";
  const levelLabel = levelKey.charAt(0).toUpperCase() + levelKey.slice(1);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/t/${code}/masters/${masterType}/merge/affected-properties`, {
      params: { source_id: sourceId, target_id: targetId, search: debounced || undefined, page, limit: PAGE_SIZE, trash: trash || undefined },
    }).then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(errMsg(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, masterType, sourceId, targetId, debounced, page, trash, tick]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ source_id: sourceId, target_id: targetId });
      if (debounced) qs.set("search", debounced);
      if (trash) qs.set("trash", "true");
      await downloadBlob(`/t/${code}/masters/${masterType}/merge/affected-properties/export?${qs.toString()}`, `merge-affected-properties.xlsx`);
      toast.success("Affected properties downloaded", { description: `${n(data?.total)} row${data?.total === 1 ? "" : "s"} · Excel` });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const total = data?.total ?? 0;
  const items = data?.items || [];
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(page * PAGE_SIZE, total);
  const title = trash ? "Affected properties in trash" : "Affected properties";

  return (
    <div id="merge-affected-panel" className="border-b bg-muted/20" data-testid="merge-affected-panel" data-bucket={trash ? "properties_trash" : "properties"}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium flex items-center gap-1.5">
            {trash ? <Archive className="h-4 w-4 text-primary" aria-hidden="true" /> : <Building2 className="h-4 w-4 text-primary" aria-hidden="true" />}
            {title}
            {data && (
              <Badge variant="secondary" className="tabular-nums" data-testid="merge-affected-total">
                {n(total)}{debounced ? ` of ${n(expected)}` : ""}
              </Badge>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            Review every {trash ? "trashed " : ""}property below before you confirm — this is exactly what the merge will rewrite.
            {data?.pairs?.length > 1 ? ` Child ${data.pairs.length - 1 === 1 ? "merge" : "merges"} included: ${data.pairs.slice(1).map((p) => `${p.source?.label} → ${p.target?.label}`).join(", ")}.` : ""}
          </p>
        </div>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="PIN, owner or house no" aria-label="Search affected properties"
            className="h-8 w-48 pl-8 text-xs" data-testid="merge-affected-search" />
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={exportXlsx} disabled={exporting || loading || !total} data-testid="merge-affected-export">
          {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />} Download Excel
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label="Close affected properties" data-testid="merge-affected-close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {error ? (
        <div className="px-3 pb-3" data-testid="merge-affected-error">
          <p className="text-xs flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/5 text-destructive px-2.5 py-2">
            <span>Could not load the affected properties: {error}</span>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setTick((t) => t + 1)} data-testid="merge-affected-retry">
              <RefreshCw className="h-3 w-3 mr-1" /> Retry
            </Button>
          </p>
        </div>
      ) : (
        <>
          <div className="max-h-72 overflow-auto border-t bg-background">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10 shadow-[inset_0_-1px_0_hsl(var(--border))]">
                <TableRow>
                  <TableHead className="text-xs">PIN</TableHead>
                  <TableHead className="text-xs">Owner</TableHead>
                  <TableHead className="text-xs">House No</TableHead>
                  <TableHead className="text-xs">Ward</TableHead>
                  <TableHead className="text-xs">{levelLabel}: current → after merge</TableHead>
                  <TableHead className="text-xs">{trash ? "Deleted" : "Collector"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && !data ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={`sk-${i}`} data-testid="merge-affected-skeleton-row">
                      {Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6" data-testid="merge-affected-empty">
                      {debounced ? `No affected property matches “${debounced}”.` : "No properties reference this record."}
                    </TableCell>
                  </TableRow>
                ) : items.map((it) => (
                  <TableRow key={it.id || it.pin} className={loading ? "opacity-60" : ""} data-testid={`merge-affected-row-${it.pin}`}>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{it.pin || "—"}</TableCell>
                    <TableCell className="text-sm">{it.owner_name || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{it.house_no || "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{it.ward?.name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell><ChangeCell item={it} levelKey={masterType} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {trash ? (
                        <span className="text-muted-foreground">{it.deleted_at ? new Date(it.deleted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}{it.deleted_by_name ? ` · ${it.deleted_by_name}` : ""}</span>
                      ) : it.collector ? (
                        <span data-testid={`merge-affected-collector-${it.pin}`}>{it.collector.name}{it.collector.employee_code ? <span className="text-muted-foreground"> ({it.collector.employee_code})</span> : null}</span>
                      ) : (
                        <span className="text-muted-foreground">Unassigned</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {total > 0 && (
            <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-muted-foreground border-t" data-testid="merge-affected-pagination">
              <span className="tabular-nums">Showing {n(from)}–{n(to)} of {n(total)}</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} data-testid="merge-affected-prev">
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </Button>
                <span className="tabular-nums px-1" data-testid="merge-affected-page">Page {n(page)} / {n(pages)}</span>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages || loading} data-testid="merge-affected-next">
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Flatten a merge report (and its cascades) into rows for the preview table. */
function flattenReport(rep, rows = [], depth = 0) {
  Object.entries(rep.changes || {}).forEach(([coll, fields]) =>
    Object.entries(fields).forEach(([field, n]) => rows.push({ level: rep.level, coll, field, n, depth })));
  (rep.cascades || []).forEach((c) => flattenReport(c, rows, depth + 1));
  return rows;
}

/**
 * Merge one zone / ward / mohalla into another.
 * props: open, onOpenChange, code, masterType, items (current master rows), nameField,
 *        initial {sourceId, targetId}, onMerged()
 */
export function MergeMasterDialog({ open, onOpenChange, code, masterType, items, nameField, parentField, parentLabelOf, initial, onMerged }) {
  const singular = MERGEABLE_MASTERS[masterType];
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [openBucket, setOpenBucket] = useState(null);   // Phase 162: which impact chip is expanded

  useEffect(() => {
    if (open) {
      setSourceId(initial?.sourceId || "");
      setTargetId(initial?.targetId || "");
      setPreview(null);
      setOpenBucket(null);
    }
  }, [open, initial]);

  const labelOf = (it) => {
    const parent = parentField && parentLabelOf ? parentLabelOf(it[parentField]) : null;
    return `${it[nameField] || it.id}${parent ? ` — ${parent}` : ""}${it.is_active === false ? " (inactive)" : ""}`;
  };
  const source = items.find((i) => i.id === sourceId);
  const target = items.find((i) => i.id === targetId);
  const crossParent = !!(parentField && source && target && source[parentField] !== target[parentField]);
  const sorted = useMemo(() => [...items].sort((a, b) => String(a[nameField] || "").localeCompare(String(b[nameField] || ""))), [items, nameField]);

  const runPreview = async () => {
    setBusy(true);
    setOpenBucket(null);
    try {
      const r = await api.post(`/t/${code}/masters/${masterType}/merge`, { source_id: sourceId, target_id: targetId, dry_run: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/t/${code}/masters/${masterType}/merge`, { source_id: sourceId, target_id: targetId, dry_run: false });
      toast.success(`Merged '${r.data.source.name}' into '${r.data.target.name}'`, {
        description: `${r.data.total_changes.toLocaleString("en-IN")} record${r.data.total_changes === 1 ? "" : "s"} updated${r.data.cascades?.length ? ` • ${r.data.cascades.length} child record${r.data.cascades.length === 1 ? "" : "s"} merged` : ""}${r.data.properties_moved != null ? ` • ${r.data.properties_moved.toLocaleString("en-IN")} propert${r.data.properties_moved === 1 ? "y" : "ies"} moved` : ""} • recorded in Master Data › Merge History`,
      });
      onOpenChange(false);
      onMerged?.(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setApplying(false);
    }
  };

  const rows = preview ? flattenReport(preview) : [];
  const ready = sourceId && targetId && sourceId !== targetId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="merge-master-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><GitMerge className="h-5 w-5 text-primary" /> Merge duplicate {singular}s</DialogTitle>
          <DialogDescription>
            Every property, staff mapping, demand, visit and report that points at the duplicate is moved to the {singular} you keep;
            duplicate names are normalised to the kept spelling; child {singular === "zone" ? "wards and mohallas" : singular === "ward" ? "mohallas" : "records"} with the same name are merged too.
            The duplicate is then removed. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Trash2 className="h-3 w-3 text-destructive" /> Duplicate to remove</Label>
            <Select value={sourceId} onValueChange={(v) => { setSourceId(v); setPreview(null); setOpenBucket(null); }}>
              <SelectTrigger className="h-10" data-testid="merge-source-select"><SelectValue placeholder={`Select ${singular}`} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {sorted.filter((i) => i.id !== targetId).map((i) => (
                  <SelectItem key={i.id} value={i.id} data-testid={`merge-source-option-${i.id}`}>{labelOf(i)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground hidden sm:block mb-2.5" aria-hidden="true" />
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Check className="h-3 w-3 text-primary" /> Keep (correct spelling)</Label>
            <Select value={targetId} onValueChange={(v) => { setTargetId(v); setPreview(null); setOpenBucket(null); }}>
              <SelectTrigger className="h-10" data-testid="merge-target-select"><SelectValue placeholder={`Select ${singular}`} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {sorted.filter((i) => i.id !== sourceId).map((i) => (
                  <SelectItem key={i.id} value={i.id} data-testid={`merge-target-option-${i.id}`}>{labelOf(i)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {crossParent && (
          <p className="text-xs flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-2.5 py-2" data-testid="merge-cross-parent-warning">
            <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-px" /> These two belong to different parents — all references will move under the kept {singular}'s parent.
          </p>
        )}

        {preview && (
          <div className="rounded-md border" data-testid="merge-preview">
            <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/40 text-sm">
              <span>
                <span className="font-medium">'{preview.source.name}'</span> → <span className="font-medium">'{preview.target.name}'</span>
              </span>
              <Badge variant="secondary" className="tabular-nums" data-testid="merge-preview-total">
                {preview.total_changes.toLocaleString("en-IN")} change{preview.total_changes === 1 ? "" : "s"}
                {preview.cascades?.length ? ` • ${preview.cascades.length} child merge${preview.cascades.length === 1 ? "" : "s"}` : ""}
              </Badge>
            </div>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground px-3 py-3" data-testid="merge-preview-empty">Nothing references '{preview.source.name}' — it will simply be removed.</p>
            ) : (
              <>
                <ImpactSummary report={preview} openBucket={openBucket} onToggle={setOpenBucket} />
                {openBucket && (
                  <AffectedPropertiesPanel key={`${openBucket}-${sourceId}-${targetId}`} code={code} masterType={masterType}
                    sourceId={sourceId} targetId={targetId} trash={!!DRILLABLE[openBucket]}
                    expected={preview.impact?.[openBucket] || 0} onClose={() => setOpenBucket(null)} />
                )}
                {preview.cascades?.length > 0 && (
                  <p className="text-xs text-muted-foreground px-3 py-2 border-b" data-testid="merge-cascade-note">
                    Child records with the same name will be merged too:{" "}
                    {preview.cascades.map((c) => `${c.source.name} → ${c.target.name}`).join(", ")}
                  </p>
                )}
                <div className="max-h-56 overflow-y-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Where</TableHead><TableHead>Field</TableHead><TableHead className="text-right">Records</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {rows.map((r, i) => (
                        <TableRow key={i} data-testid={`merge-preview-row-${r.coll}-${r.field}`}>
                          <TableCell className="text-sm" style={{ paddingLeft: `${12 + r.depth * 16}px` }}>
                            {r.depth > 0 && <span className="text-muted-foreground mr-1">↳ {r.level}:</span>}{r.coll.replace(/_/g, " ")}
                          </TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">{humanField(r.field)}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.n.toLocaleString("en-IN")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
            {preview.skipped?.length > 0 && (
              <p className="text-xs flex items-start gap-1.5 px-3 py-2 border-t text-amber-900 bg-amber-50" data-testid="merge-skipped-note">
                <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-px" />
                {preview.skipped.length} collection{preview.skipped.length === 1 ? "" : "s"} could not be scanned and will be left untouched: {preview.skipped.map((s) => s.collection.replace(/_/g, " ")).join(", ")}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="merge-cancel-button">Cancel</Button>
          {!preview ? (
            <Button onClick={runPreview} disabled={!ready || busy} data-testid="merge-preview-button">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <GitMerge className="h-4 w-4 mr-1.5" />} Preview changes
            </Button>
          ) : (
            <Button variant="destructive" onClick={apply} disabled={applying} data-testid="merge-apply-button">
              {applying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Merge and remove '{preview.source.name}'
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Amber banner listing detected duplicate groups with one-click "Merge" pre-fills. */
export function DuplicateMastersBanner({ groups, singular, onMerge }) {
  if (!groups?.length) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/70 px-3 py-2.5 mb-3 text-sm" data-testid="master-duplicates-banner">
      <p className="font-medium text-amber-900 flex items-center gap-1.5">
        <ShieldAlert className="h-4 w-4 text-amber-600" />
        {groups.length} possible duplicate {singular}{groups.length === 1 ? "" : "s"} — same name spelled differently
      </p>
      <ul className="mt-1.5 space-y-1">
        {groups.map((g) => {
          const keep = g.items.find((i) => i.id === g.suggested_keep_id) || g.items[0];
          const dups = g.items.filter((i) => i.id !== keep.id);
          return (
            <li key={`${g.key}-${g.parent_id || ""}`} className="flex flex-wrap items-center gap-1.5" data-testid={`master-duplicate-group-${g.key}`}>
              {g.items.map((i, idx) => (
                <React.Fragment key={i.id}>
                  {idx > 0 && <span className="text-muted-foreground text-xs">↔</span>}
                  <Badge variant={i.id === keep.id ? "default" : "outline"} className="font-normal">{i.name}</Badge>
                </React.Fragment>
              ))}
              {g.parent_name && <span className="text-xs text-muted-foreground">in {g.parent_name}</span>}
              <span className="text-xs text-muted-foreground ml-1">keep “{keep.name}”</span>
              {dups.map((d) => (
                <Button key={d.id} size="sm" variant="outline" className="h-7 text-xs ml-auto sm:ml-1 bg-white"
                  onClick={() => onMerge({ sourceId: d.id, targetId: keep.id })} data-testid={`master-duplicate-merge-${d.id}`}>
                  <GitMerge className="h-3 w-3 mr-1" /> Merge “{d.name}” into “{keep.name}”
                </Button>
              ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
